import { useCallback } from "react";
import { getUpdatedMeetingValue } from "../helpers/getUpdatedValue";
import { useAppSelector } from "../../../services/redux/hooks/use-selector";
import { selectMeetingValue } from "../redux/selectors";
import { setMeetingValue } from "../redux/slice";
import { useUpdateStorageValue } from "../../../app/state/storageObjectSlice/hook/use-update-storage-value";
import { generateFinalResultText } from "../helpers/generateFinalResultText";
import {
  ChangeHandlerValue,
  TSpeakerActions,
  TTranscriptionInfoInputs,
} from "../types";
import {
  MeetingsValue,
  TMeetingInfo,
  TSpeakerContent,
} from "../../../services/api/instances/meetingsApi/types";
import { CUSTOM_SPEAKER_NAME, SPEAKER_UNDEFINED } from "../constants";
import { useTranslation } from "react-i18next";
import { generateId } from "../../../shared/utils/generateId";

export const useSpeakerEditor = () => {
  const { saveValue } = useUpdateStorageValue("meetings", setMeetingValue);
  const value = useAppSelector(selectMeetingValue);
  const { t } = useTranslation();
  const handleAddNewSpeaker = useCallback(() => {
    if (!value?.meetings_object) return;
    const speakers = value.meetings_object.transcription.filter((speaker) =>
      speaker.speaker_group_key.startsWith(CUSTOM_SPEAKER_NAME)
    );
    const newValue = getUpdatedMeetingValue(value, {
      transcription: [
        ...value?.meetings_object?.transcription,
        {
          id: `speaker_${generateId()}`,
          speaker_group_key: `${CUSTOM_SPEAKER_NAME}${speakers.length + 1}`,
          speaker_name: `Новый спикер ${speakers.length + 1}`,
          isActive: false,
          speech_content: null,
        },
      ],
    });

    saveValue(newValue);
  }, [value]);

  const handleSpeakerActions = (
    id: string,
    handlerValue: ChangeHandlerValue,
    type: TSpeakerActions
  ) => {
    if (!value?.meetings_object) return;
    const currentValue = value.meetings_object;
    let newValue: MeetingsValue | null;
    switch (type) {
      case "name_change": {
        const newTranscription = currentValue.transcription.map((speaker) =>
          speaker.speaker_group_key === id
            ? { ...speaker, speaker_name: handlerValue as string }
            : speaker
        );
        newValue = getUpdatedMeetingValue(value, {
          transcription: newTranscription,
          final_result: generateFinalResultText(
            currentValue?.meeting_info,
            newTranscription
          ),
        });
        saveValue(newValue, true);
        return;
      }
      case "active_change": {
        newValue = getUpdatedMeetingValue(value, {
          transcription: currentValue.transcription.map((speaker) =>
            speaker.speaker_group_key === id
              ? { ...speaker, isActive: handlerValue as boolean }
              : speaker
          ),
        });
        saveValue(newValue);
        return;
      }
      default:
        return;
    }
  };

  const handleDeleteSpeaker = (
    currentSpeaker: Omit<TSpeakerContent, "speech_content">
  ) => {
    if (!value?.meetings_object) return;
    const currentValue = value.meetings_object;
    const newTranscript = currentValue.transcription
      .map((speaker) =>
        speaker.speaker_group_key === currentSpeaker.speaker_group_key
          ? {
              ...speaker,
              speaker_group_key: SPEAKER_UNDEFINED,
              speaker_name: t("meetings.speaker_undefined"),
            }
          : speaker
      )
      .filter((item) => item.id !== currentSpeaker.id);
    const newValue = getUpdatedMeetingValue(value, {
      transcription: newTranscript,
      final_result: generateFinalResultText(
        currentValue?.meeting_info,
        newTranscript
      ),
    });
    saveValue(newValue);
  };

  const handleChangeTranscriptionInfo = (
    inputValue: ChangeHandlerValue,
    type: TTranscriptionInfoInputs
  ) => {
    if (!value?.meetings_object) return;
    const currentValue = value.meetings_object;
    let newValue: MeetingsValue | null;
    switch (type) {
      case "title": {
        const newInfo: TMeetingInfo = {
          ...currentValue.meeting_info,
          meeting_name: inputValue as string,
          _user_edited: true,
        };
        newValue = getUpdatedMeetingValue(value, {
          meeting_info: newInfo,
          final_result: generateFinalResultText(
            newInfo,
            currentValue?.transcription
          ),
        });
        saveValue(newValue, true);
        return;
      }
      case "date": {
        const newInfo: TMeetingInfo = {
          ...currentValue.meeting_info,
          meeting_date: (inputValue as Date).toISOString(),
          _user_edited: true,
        };
        newValue = getUpdatedMeetingValue(value, {
          meeting_info: newInfo,
          final_result: generateFinalResultText(
            newInfo,
            currentValue?.transcription
          ),
        });
        saveValue(newValue);
        return;
      }
      case "description": {
        const newInfo: TMeetingInfo = {
          ...currentValue.meeting_info,
          meeting_description: inputValue as string,
          _user_edited: true,
        };
        newValue = getUpdatedMeetingValue(value, {
          meeting_info: newInfo,
          final_result: generateFinalResultText(
            newInfo,
            currentValue?.transcription
          ),
        });
        saveValue(newValue, true);
        return;
      }
      default:
        return;
    }
  };

  return {
    handleSpeakerActions,
    handleDeleteSpeaker,
    handleAddNewSpeaker,
    handleChangeTranscriptionInfo,
  };
};
